/**
 * engine/juros.ts — os juros do saldo devedor do PJ (linha "Despesas Financeiras" do Fluxo
 * mensal), pelo "Memorial de calculo FinCast", seção 5 — Fluxo pelo memorial, 15/10/2026.
 *
 * Decisões do usuário (14/10/2026, caixa): granularidade MENSAL e juros "Completo com Banco
 * Central". Em consequência:
 *   - os juros do mês incidem sobre o saldo de ABERTURA do mês (o PJ que entra no mês começa a
 *     pagar no seguinte — o "juros-dia" do memorial some na granularidade mensal);
 *   - não são incorporados ao saldo (memorial: "cobrados mensalmente … Não são incorporados");
 *   - as taxas vêm por parâmetro (`TaxasMensais`): o realizado do Banco Central (SGS 4391 CDI no
 *     mês, 204 Poupança — só a remuneração adicional, sem a TR, desde 16/10/2026 ("Trocar para a 204 nos juros
 *     também"; a 196 já incluía a TR, que a correção TR somava de novo) —, 7811 TR no mês) e, para o futuro, a
 *     projeção. Sem projeção para o mês, repete o último realizado (decisão do plano, P10).
 *
 * Fórmulas (memorial, seção 5), com `p` = % do indexador e `nom` = taxa nominal a.a.:
 *   Poupança: (saldo + correção TR) × (poupança do mês × p + nom ÷ 12)
 *   CDI:      saldo × [(1 + TDI × p)^du × (1 + nom)^(du/252) − 1], TDI = (1 + CDI do mês)^(1/du) − 1
 *             (o "dia útil a dia útil" do memorial, com o CDI do mês repartido igualmente nos du dias)
 *   Nenhum:   saldo × nom ÷ 12
 *   TR:       correção = saldo × TR do mês (30 dias), paga no mês
 *
 * Puro: sem relógio, sem rede. Quem busca as séries é a camada de dados.
 */

import { diasUteis } from './datas';
import type { IndexadorSaldo, IndexadorTaxa, Lev, Ym } from './tipos';

export type { IndexadorSaldo, IndexadorTaxa };

/** Os 4 parâmetros de juros do cenário (EL-206 a EL-209). */
export interface ParamJuros {
  indexadorTaxa: IndexadorTaxa;
  /** % do indexador (100 = 100% do CDI). */
  pctIndexador: number;
  /** taxa nominal ao ano, em % (3,5 = 3,5% a.a.). */
  taxaNominalAa: number;
  indexadorSaldo: IndexadorSaldo;
}

/** As taxas de UM mês, em % no mês (como o Banco Central publica). */
export interface TaxaMes {
  cdiMes?: number;
  poupMes?: number;
  trMes?: number;
}

/** Uma linha da projeção: a partir do mês `desde`, o indexador vale `valor`. CDI em % a.a.; Poupança e TR em % a.m. */
export interface ProjecaoTaxa {
  indexador: 'cdi' | 'poupanca' | 'tr';
  desde: Ym;
  valor: number;
}

export interface TaxasMensais {
  /** realizado do Banco Central, por mês (`ym`) — só meses fechados */
  realizado: Record<number, TaxaMes>;
  projecao?: ProjecaoTaxa[];
}

export type OrigemTaxa = 'realizado' | 'projecao' | 'ultimo' | 'ausente';

export interface JurosMes {
  juros: number;
  correcaoTR: number;
  /** juros + correção TR — o valor da linha "Despesas Financeiras" */
  total: number;
  /** de onde veio a taxa do indexador da taxa (e da TR, se usada): o pior dos dois */
  origem: OrigemTaxa;
}

const INDEXADORES_TAXA: readonly IndexadorTaxa[] = ['cdi', 'poupanca', 'nenhum'];

/**
 * Os parâmetros de juros do cenário, ou `null` quando não estão preenchidos — aí a linha fica
 * zerada e a tela avisa (decisão do plano: não volta ao provisório de 0,8% a.m.).
 */
export function paramJurosDe(lev: Lev | null | undefined): ParamJuros | null {
  if (!lev) return null;
  const it = lev.jurosIndexadorTaxa, nom = lev.jurosTaxaNominalAa;
  if (!it || !INDEXADORES_TAXA.includes(it) || typeof nom !== 'number' || !Number.isFinite(nom)) return null;
  const pct = lev.jurosPctIndexador;
  return {
    indexadorTaxa: it,
    pctIndexador: typeof pct === 'number' && Number.isFinite(pct) ? pct : 100,
    taxaNominalAa: nom,
    indexadorSaldo: lev.jurosIndexadorSaldo === 'tr' ? 'tr' : 'nenhum',
  };
}

const ORDEM: OrigemTaxa[] = ['realizado', 'projecao', 'ultimo', 'ausente'];
const pior = (a: OrigemTaxa, b: OrigemTaxa): OrigemTaxa => (ORDEM.indexOf(a) >= ORDEM.indexOf(b) ? a : b);

/**
 * A taxa de um indexador no mês, em FRAÇÃO no mês: o realizado; senão a projeção (a linha de
 * maior `desde` ≤ mês); senão o último realizado anterior; senão zero ("ausente").
 */
export function taxaDoMes(taxas: TaxasMensais | null | undefined, ind: 'cdi' | 'poupanca' | 'tr', ym: Ym): { valor: number; origem: OrigemTaxa } {
  const campo = ind === 'cdi' ? 'cdiMes' : ind === 'poupanca' ? 'poupMes' : 'trMes';
  const real = taxas?.realizado?.[ym]?.[campo];
  if (typeof real === 'number' && Number.isFinite(real)) return { valor: real / 100, origem: 'realizado' };
  const proj = (taxas?.projecao ?? [])
    .filter(p => p.indexador === ind && p.desde <= ym && Number.isFinite(p.valor))
    .sort((a, b) => b.desde - a.desde)[0];
  if (proj) {
    /* o CDI é projetado ao ano: vira taxa do mês pelos dias úteis dele */
    const v = ind === 'cdi' ? Math.pow(1 + proj.valor / 100, diasUteis(ym) / 252) - 1 : proj.valor / 100;
    return { valor: v, origem: 'projecao' };
  }
  const anteriores = Object.keys(taxas?.realizado ?? {}).map(Number)
    .filter(k => k < ym && typeof taxas!.realizado[k]?.[campo] === 'number').sort((a, b) => b - a);
  if (anteriores.length) return { valor: (taxas!.realizado[anteriores[0]!]![campo] as number) / 100, origem: 'ultimo' };
  return { valor: 0, origem: 'ausente' };
}

/**
 * Os juros (e a correção TR) do mês `ym` sobre o saldo devedor `saldo`.
 *
 * @param adicionalAa  o adicional de CARÊNCIA do ciclo em que o mês cai (% a.a.; 15/10/2026). Soma à taxa
 *   nominal em todos os indexadores — no da Poupança, é exatamente o "+ adicional de carência" do memorial.
 */
export function jurosDoMes(saldo: number, ym: Ym, p: ParamJuros, taxas: TaxasMensais | null | undefined, adicionalAa = 0): JurosMes {
  if (!(saldo > 0)) return { juros: 0, correcaoTR: 0, total: 0, origem: 'realizado' };
  const pct = p.pctIndexador / 100, nom = (p.taxaNominalAa + (adicionalAa || 0)) / 100;
  let origem: OrigemTaxa = 'realizado';
  let correcaoTR = 0;
  if (p.indexadorSaldo === 'tr') {
    const tr = taxaDoMes(taxas, 'tr', ym);
    origem = pior(origem, tr.origem);
    correcaoTR = saldo * tr.valor;
  }
  let juros = 0;
  if (p.indexadorTaxa === 'cdi') {
    const cdi = taxaDoMes(taxas, 'cdi', ym);
    origem = pior(origem, cdi.origem);
    const du = diasUteis(ym);
    const tdi = du > 0 ? Math.pow(1 + cdi.valor, 1 / du) - 1 : 0;
    juros = saldo * (Math.pow(1 + tdi * pct, du) * Math.pow(1 + nom, du / 252) - 1);
  } else if (p.indexadorTaxa === 'poupanca') {
    const poup = taxaDoMes(taxas, 'poupanca', ym);
    origem = pior(origem, poup.origem);
    juros = (saldo + correcaoTR) * (poup.valor * pct + nom / 12);
  } else {
    juros = saldo * (nom / 12);
  }
  return { juros, correcaoTR, total: juros + correcaoTR, origem };
}
